// src/utils/recurringOccurrenceUtils.js
//
// SINGLE SOURCE OF TRUTH for how a job's startDate / endDate relate to the
// days it renders on.
//
// THE BUG THIS FIXES
// ------------------
// A recurring job's `endDate` was being treated as a PER-OCCURRENCE DURATION
// on several code paths. So a weekly job with startDate 2026-08-03 and an
// auto-filled endDate of 2026-08-31 rendered 29 tiles for EVERY occurrence -
// the same job duplicated across the whole span, and overlapping the next
// occurrence's tiles on top of that.
//
// THE RULE
// --------
//   * ONE-TIME job:  endDate IS a duration. 8/3 -> 8/5 renders on 8/3, 8/4, 8/5.
//   * RECURRING job: endDate is NOT a duration. Each occurrence is ONE day.
//                    "When does the series stop?" lives in recurrence.endsOn.
//
// Every expansion path must call getOccurrenceSpanDays() instead of doing its
// own endDate.diff(startDate) math.

import moment from "moment";

export const toDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim().slice(0, 10);
    return moment(trimmed, "YYYY-MM-DD", true).isValid() ? trimmed : "";
  }
  const m = moment(value?.toDate ? value.toDate() : value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const getRecurrenceType = (job) => {
  let type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence ??
        job?.reccurance ??
        job?.recurring ??
        job?.repeat ??
        "none";

  type = String(type || "none").toLowerCase().trim();

  if (!type || type === "one-time" || type === "onetime") return "none";
  if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
  if (type === "customdates" || type === "custom dates") return "customDates";

  return type;
};

export const isRecurring = (job) => getRecurrenceType(job) !== "none";

/**
 * How many EXTRA days past the occurrence's start day this job should render.
 * 0 means "single day tile".
 *
 * Recurring jobs ALWAYS return 0 - their endDate is a series boundary, never
 * a duration. This is the guard that stops one occurrence from painting
 * itself across dozens of days.
 */
export const getOccurrenceSpanDays = (job) => {
  if (isRecurring(job)) return 0;

  const start = toDateKey(job?.startDate);
  const end = toDateKey(job?.endDate);

  if (!start || !end || end <= start) return 0;

  return Math.max(
    moment(end, "YYYY-MM-DD").diff(moment(start, "YYYY-MM-DD"), "days"),
    0
  );
};

/**
 * Where the SERIES stops generating. Reads recurrence.endsOn only - it must
 * NOT fall back to the top-level job.endDate, because that field is what got
 * auto-populated and silently truncated / extended series.
 */
export const getSeriesEndKey = (job) => {
  const recurrence = typeof job?.recurrence === "object" ? job.recurrence : {};
  return toDateKey(recurrence.endsOn || recurrence.endDate || job?.recurrenceEndDate);
};

// ---------------------------------------------------------------------------
// OCCURRENCE IDENTITY  (the fix for "rescheduled occurrence can't be completed")
// ---------------------------------------------------------------------------
//
// A recurring occurrence has TWO dates once a user edits just that one date
// via "Edit this job only":
//
//   canonical  - the date the recurrence RULE produced (e.g. 2026-08-10).
//                This is what occurrenceOverrides is keyed by, what
//                excludedDates records, and what every expander generates.
//                It never changes, no matter how many times the occurrence
//                is rescheduled.
//   scheduled  - the day it was moved to and actually renders on (2026-08-07).
//
// completedDates / invoicedDates / completedAtByDate MUST be keyed by the
// CANONICAL date. Writing the scheduled date instead is what broke this:
// the write said "2026-08-07" but every expander asked "is 2026-08-10
// completed?", so the occurrence never went red on Calendar, never dropped
// off Home, and drifted out of sync with excludedDates. Keying off canonical
// also means rescheduling the SAME occurrence a second time doesn't orphan
// its completion.
//
// Readers still accept the scheduled date as a fallback so occurrences that
// were completed before this fix keep working.

/** The permanent identity of an occurrence: the date the series generated. */
export const getCanonicalOccurrenceKey = (job) =>
  toDateKey(job?.originalOccurrenceDate || job?.occurrenceDate || job?.startDate);

/** The day the occurrence actually lands on, after any reschedule. */
export const getScheduledOccurrenceKey = (job) =>
  toDateKey(job?.occurrenceDate || job?.startDate);

/**
 * ALWAYS use this to build the key written into completedDates,
 * invoicedDates and completedAtByDate.
 */
export const getCompletionOccurrenceKey = (job) => getCanonicalOccurrenceKey(job);

/** Canonical first, then the scheduled day for pre-fix records. */
export const getOccurrenceKeyCandidates = (job) =>
  Array.from(
    new Set(
      [getCanonicalOccurrenceKey(job), getScheduledOccurrenceKey(job)].filter(
        Boolean
      )
    )
  );

/** Is this RENDERED occurrence listed in `dates` under either of its keys? */
export const occurrenceIsInDateList = (job, dates) => {
  const set = new Set((dates || []).map(toDateKey).filter(Boolean));
  if (!set.size) return false;
  return getOccurrenceKeyCandidates(job).some((key) => set.has(key));
};

/** Where a canonical occurrence date got moved to, or "" if never moved. */
export const getOverrideScheduledKey = (masterJob, canonicalKey) => {
  const overrides =
    masterJob && typeof masterJob.occurrenceOverrides === "object"
      ? masterJob.occurrenceOverrides
      : null;

  const override = overrides?.[toDateKey(canonicalKey)];
  if (!override) return "";

  return toDateKey(override.startDate || override.occurrenceDate);
};

/** Reverse lookup: which canonical occurrence was moved onto this day? */
export const getCanonicalKeyForScheduledKey = (masterJob, scheduledKey) => {
  const overrides =
    masterJob && typeof masterJob.occurrenceOverrides === "object"
      ? masterJob.occurrenceOverrides
      : null;

  if (!overrides) return "";

  const target = toDateKey(scheduledKey);
  if (!target) return "";

  const hit = Object.entries(overrides).find(
    ([, override]) =>
      toDateKey(override?.startDate || override?.occurrenceDate) === target
  );

  return hit ? toDateKey(hit[0]) : "";
};

/**
 * For EXPANSION code, where all you have is the master doc plus the canonical
 * date the recurrence rule just produced. Returns a predicate that answers
 * "is this occurrence in that list of dates?" without caring whether the date
 * was recorded under its canonical or its rescheduled key.
 *
 *   const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates);
 *   dates.filter((dateKey) => !isCompleted(dateKey));
 */
export const makeOccurrenceDateMatcher = (masterJob, dates) => {
  const set = new Set((dates || []).map(toDateKey).filter(Boolean));

  return (canonicalKey) => {
    const key = toDateKey(canonicalKey);
    if (!key || !set.size) return false;
    if (set.has(key)) return true;

    const scheduled = getOverrideScheduledKey(masterJob, key);
    return Boolean(scheduled) && set.has(scheduled);
  };
};

// ---------------------------------------------------------------------------
// SERIES STATE  (the second half of the same bug)
// ---------------------------------------------------------------------------
//
// "Edit this job only" snapshots the WHOLE rendered job into
// occurrenceOverrides[canonicalDate]. That snapshot used to include the
// series' bookkeeping arrays, so a rendered occurrence got the values as they
// were AT EDIT TIME - completedDates: [] - which then shadowed the live values
// from the master doc. Marking the occurrence complete wrote to Firestore
// correctly, but the UI kept reading the frozen empty array and showed the job
// as still open.
//
// These fields belong to the DOCUMENT, never to one occurrence's override.

export const SERIES_STATE_FIELDS = [
  "completedDates",
  "invoicedDates",
  "excludedDates",
  "completedAtByDate",
  "occurrenceOverrides",
  "occurrenceNotes",
  "notes",
  "supervisorNotes",
  "seriesSplitAfter",
  "seriesEndedAt",
];

/** Drop series-level state before it can be frozen into an override. */
export const stripSeriesStateFields = (obj = {}) => {
  const next = { ...obj };
  SERIES_STATE_FIELDS.forEach((key) => delete next[key]);
  return next;
};

/**
 * Put the LIVE series state back on a rendered occurrence, overwriting
 * anything an old (already-saved) override snapshot brought with it. This is
 * what heals existing bad data without a migration.
 */
export const restoreSeriesStateFields = (renderedJob = {}, masterJob = {}) => {
  const next = { ...renderedJob };

  SERIES_STATE_FIELDS.forEach((key) => {
    if (masterJob && Object.prototype.hasOwnProperty.call(masterJob, key)) {
      next[key] = masterJob[key];
    } else {
      delete next[key];
    }
  });

  return next;
};

export const getCompletedDateSet = (job) =>
  new Set((job?.completedDates || []).map(toDateKey).filter(Boolean));

export const getInvoicedDateSet = (job) =>
  new Set((job?.invoicedDates || []).map(toDateKey).filter(Boolean));

export const getExcludedDateSet = (job) =>
  new Set((job?.excludedDates || []).map(toDateKey).filter(Boolean));

/** Which occurrence a rendered job object represents. */
export const getOccurrenceKey = (job) => getCanonicalOccurrenceKey(job);

/**
 * Is THIS occurrence (not the whole series) finished?
 *
 * Checks the canonical key AND the rescheduled key, so an occurrence that was
 * moved to another day is still recognised as complete regardless of which key
 * the completion was recorded under.
 */
export const isOccurrenceCompleted = (job) => {
  if (!isRecurring(job)) return job?.status === "Completed";
  return occurrenceIsInDateList(job, job?.completedDates);
};

/** Is THIS occurrence already invoiced? */
export const isOccurrenceInvoiced = (job) => {
  if (!isRecurring(job)) return job?.status === "Invoice Sent";
  return occurrenceIsInDateList(job, job?.invoicedDates);
};

/**
 * Strip an auto-populated endDate before writing to Firestore.
 *
 * Rules:
 *   - endDate blank  -> stays blank (do NOT helpfully fill it in)
 *   - endDate === startDate -> blanked, it carries no information
 *   - endDate < startDate   -> blanked, it's garbage
 *   - recurring job  -> endDate is NEVER a span. If a user typed one we move
 *                       it to recurrence.endsOn (the series end) instead,
 *                       which is what they actually meant.
 *   - one-time job with a real, later endDate -> kept as-is (real multi-day job)
 */
export const sanitizeJobDates = (job = {}) => {
  const next = { ...job };

  const start = toDateKey(next.startDate);
  const end = toDateKey(next.endDate);

  if (start) next.startDate = start;

  if (!end || !start || end <= start) {
    next.endDate = "";
  } else if (isRecurring(next)) {
    // A date typed into "End Date" on a repeating job means "stop repeating
    // after this". Move it where the expanders actually read it from.
    const recurrence =
      typeof next.recurrence === "object" ? { ...next.recurrence } : {};

    if (!toDateKey(recurrence.endsOn)) {
      recurrence.endsOn = end;
      next.recurrence = recurrence;
    }

    next.endDate = "";
  } else {
    next.endDate = end;
  }

  return next;
};

/**
 * Expand every completed occurrence of a recurring job into its own row.
 * Used by SendInvoices so a single finished occurrence becomes an invoiceable
 * line without touching the rest of the series.
 */
export const expandCompletedOccurrences = (job) => {
  if (!isRecurring(job)) {
    return job?.status === "Completed" ? [job] : [];
  }

  const invoiced = getInvoicedDateSet(job);

  // Fold every recorded completion back to its CANONICAL date. Entries written
  // before the canonical-key fix hold the rescheduled day instead, and without
  // this the same occurrence could surface as two separate invoiceable rows.
  const canonicalKeys = new Set();
  getCompletedDateSet(job).forEach((dateKey) => {
    canonicalKeys.add(getCanonicalKeyForScheduledKey(job, dateKey) || dateKey);
  });

  return Array.from(canonicalKeys)
    .map((canonicalKey) => ({
      canonicalKey,
      // The row shows the day the work was actually scheduled for, which for a
      // rescheduled occurrence is NOT the canonical date.
      scheduledKey: getOverrideScheduledKey(job, canonicalKey) || canonicalKey,
    }))
    .filter(
      ({ canonicalKey, scheduledKey }) =>
        !invoiced.has(canonicalKey) && !invoiced.has(scheduledKey)
    )
    .sort((a, b) => a.scheduledKey.localeCompare(b.scheduledKey))
    .map(({ canonicalKey, scheduledKey }) => ({
      ...job,
      startDate: scheduledKey,
      endDate: scheduledKey,
      occurrenceDate: scheduledKey,
      originalOccurrenceDate: canonicalKey,
      status: "Completed",
      completedAt:
        job?.completedAtByDate?.[canonicalKey] ||
        job?.completedAtByDate?.[scheduledKey] ||
        job?.completedAt ||
        null,
      // Rows share a Firestore doc id, so give the UI a stable unique key.
      // Keyed by CANONICAL date so "Mark Invoice Sent" writes the same key
      // completion did.
      __rowKey: `${job.id}__${canonicalKey}`,
      __occurrenceRow: true,
    }));
};

/**
 * Reload the page WITHOUT losing which tab/view the user was on.
 *
 * Pages persist their own tab/view into sessionStorage before calling this,
 * and re-read it on mount, so a reload lands the user exactly where they
 * were - in Calendar if they were in Calendar, not bounced back to Jobs.
 */
export const REFRESH_SCROLL_KEY = "priority_refresh_scroll_y";

export const refreshKeepingView = (viewState = {}) => {
  try {
    Object.entries(viewState).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      sessionStorage.setItem(key, String(value));
    });
    sessionStorage.setItem(REFRESH_SCROLL_KEY, String(window.scrollY || 0));
  } catch (e) {
    // sessionStorage unavailable (private browsing) - reload anyway.
  }

  window.location.reload();
};

/** Call once on mount to restore scroll after refreshKeepingView(). */
export const restoreScrollAfterRefresh = () => {
  try {
    const saved = sessionStorage.getItem(REFRESH_SCROLL_KEY);
    if (saved === null) return;
    sessionStorage.removeItem(REFRESH_SCROLL_KEY);
    const y = Number(saved);
    if (!Number.isNaN(y)) {
      window.requestAnimationFrame(() => window.scrollTo(0, y));
    }
  } catch (e) {
    // ignore
  }
};
